import type { ReactNode } from 'react';
import { cx } from '../lib/cx';
import { type Tone, useCurrentSection } from '../lib/sections';
import styles from './Page.module.css';

/**
 * Page title block: the section's line badge, big title, a quiet line of context, actions on the
 * right, and the line's colour drawn underneath.
 */
export function PageHeader({
  title,
  meta,
  actions,
}: {
  title: string;
  meta?: ReactNode;
  actions?: ReactNode;
}) {
  const section = useCurrentSection();
  return (
    <header className={styles.header}>
      <div className={styles.headerRow}>
        <div>
          <h1 className={styles.title}>
            {section ? (
              <span className={styles.badge} aria-hidden="true">
                0{section.key}
              </span>
            ) : null}
            {title}
          </h1>
          {meta ? <p className={styles.meta}>{meta}</p> : null}
        </div>
        {actions ? <div className={styles.actions}>{actions}</div> : null}
      </div>
      <span className={styles.band} aria-hidden="true" />
    </header>
  );
}

/** A titled block inside a page. `tone` gives it another section's colour (used on Oggi). */
export function Section({
  title,
  aside,
  tone,
  children,
  className,
}: {
  title?: string;
  aside?: ReactNode;
  tone?: Tone;
  children: ReactNode;
  className?: string;
}) {
  return (
    <section className={cx(styles.section, className)} data-tone={tone}>
      {title || aside ? (
        <div className={styles.sectionHead}>
          {title ? <h2 className={styles.sectionTitle}>{title}</h2> : <span />}
          {aside}
        </div>
      ) : null}
      {children}
    </section>
  );
}

/** What an empty list says: one calm sentence, an optional hint. */
export function Empty({ children, hint }: { children: ReactNode; hint?: ReactNode }) {
  return (
    <div className={styles.empty}>
      <p>{children}</p>
      {hint ? <p className={styles.emptyHint}>{hint}</p> : null}
    </div>
  );
}

export function Kbd({ children }: { children: ReactNode }) {
  return <kbd className={styles.kbd}>{children}</kbd>;
}
